"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.validateTimeSlotActiveTimes = exports.validateActivityTimesOnTimeSlot = exports.splitSlot = exports.splitActivityTimes = exports.MIDNIGHT = exports.getDurationOfTimeRange = void 0;
const dayjs_1 = __importDefault(require("dayjs"));
const formatter_1 = require("../formatter");
const time_slots_1 = require("../time-slots");
const types_1 = require("../types");
const validation_1 = require("../validation");
const HOURS_IN_DAY = 24;
const MINUTES_IN_HOUR = 60;
const SECONDS_IN_MINUTE = 60;
const MINUTES_IN_24_HOURS = HOURS_IN_DAY * MINUTES_IN_HOUR;
const MIDNIGHT = '00:00:00';
exports.MIDNIGHT = MIDNIGHT;
/**
 * Splits activity times that span across midnight into two separate activity times.
 * @param activityTimes {T[]} - The activity times to split.
 * @returns {T[]} The split activity times.
 */
function splitActivityTimes(activityTimes) {
    const splittedActivityTimes = [];
    for (const activityTime of activityTimes) {
        // In case of over-night activity time, for example 20:00 PM - 04:00 AM the next day,
        // we need to split it into 2 partial activity times, one for the first day and one for the next day.
        if (activityTime.open >= activityTime.close && activityTime.close !== MIDNIGHT) {
            const newAt = Object.assign({}, activityTime);
            // The day index of the next day in the week.
            // For example 8 (Sunday), index 7 => 2 (Monday), index 0;
            const dayIndex = newAt.dayOfWeek % types_1.DAYS_OF_WEEK_TO_BOND_DAYS_OF_WEEK.length;
            newAt.open = MIDNIGHT;
            newAt.dayOfWeek = types_1.DAYS_OF_WEEK_TO_BOND_DAYS_OF_WEEK[dayIndex];
            splittedActivityTimes.push(Object.assign(Object.assign({}, activityTime), { close: MIDNIGHT }), newAt);
        }
        else {
            splittedActivityTimes.push(activityTime);
        }
    }
    return splittedActivityTimes;
}
exports.splitActivityTimes = splitActivityTimes;
/**
 * Validates the activity times on a given time slot.
 * @param slot {T} - The time slot to validate.
 * @param activityTimes {T2[]} - The activity times to validate.
 * @param intersect {boolean} - A flag indicating whether the activity times should intersect with the time slot.
 * @returns {IValidateActivityTimesOnTimeSlotResult<T2>} The result of the validation.
 */
function validateTimeSlotActiveTimes(slot, activityTimes, intersect = false) {
    // If no activity times are provided, return the duration of the time slot.
    if (!(activityTimes === null || activityTimes === void 0 ? void 0 : activityTimes.length)) {
        return {
            duration: getDurationOfTimeRange({
                start: slot.startTime,
                end: slot.endTime,
                midnight: (0, time_slots_1.getDiff)(slot.startDate, slot.endDate, types_1.TimeUnit.DAY),
            }),
            activityTimes: [],
        };
    }
    // Split activity times that span across over-night into two separate activity times.
    const splittedActivityTimes = splitActivityTimes(activityTimes);
    // Split the time slot into multiple time slots.
    const timeSlots = splitSlot(slot);
    const validActivityTimes = [];
    let duration = 0;
    for (const slot of timeSlots) {
        for (const activityTime of splittedActivityTimes) {
            const isActive = isActiveOnSlotTime(slot, activityTime, intersect);
            if (isActive.valid) {
                validActivityTimes.push(activityTime);
                duration += isActive.value;
            }
        }
    }
    return { duration, activityTimes: validActivityTimes };
}
exports.validateTimeSlotActiveTimes = validateTimeSlotActiveTimes;
/**
 * Gets the total duration of the active activity times on a given time slot.
 * @param slot {T} - The time slot to validate.
 * @param activityTimes {T2[]} - The activity times to validate.
 * @param intersect {boolean} - A flag indicating whether the activity times should intersect with the time slot.
 * @returns {number} The total duration of the active activity times on the time slot.
 */
function validateActivityTimesOnTimeSlot(slot, activityTimes, intersect = false) {
    return validateTimeSlotActiveTimes(slot, activityTimes, intersect).duration;
}
exports.validateActivityTimesOnTimeSlot = validateActivityTimesOnTimeSlot;
/**
 * Checks if an activity is active on a given time slot.
 * @param slot {ITimeSlot} - The time slot to check.
 * @param activityTime {T} - The activity time to check.
 * @param intersect {boolean} - A flag indicating whether the activity time should intersect with the time slot.
 * @returns {ValidationReason<number>} A validation reason object containing the result of the check.
 */
function isActiveOnSlotTime(slot, activityTime, intersect = false) {
    return isActiveOn(activityTime, slot.startDate, slot.startTime, slot.endTime, intersect);
}
/**
 * Checks if an activity is active on a given date.
 * @param activityTime {T} - The activity time to check.
 * @param date {string} - The date to check.
 * @param start {string} - The start time of the time range.
 * @param end {string} - The end time of the time range.
 * @param intersect {boolean} - A flag indicating whether the activity time should intersect with the time range.
 * @returns {ValidationReason<number>} A validation reason object containing the result of the check.
 */
function isActiveOn(activityTime, date, start, end, intersect = false) {
    if (activityTime.availabilityEndDate &&
        activityTime.availabilityStartDate &&
        !(0, validation_1.isInMonthDayRange)(activityTime, date)) {
        return {
            valid: false,
            reason: `Not in date range ${activityTime.availabilityEndDate} <=> ${activityTime.availabilityStartDate}`,
            value: 0,
        };
    }
    if (activityTime.dayOfWeek !== null && !isSameDayAsActivity(activityTime, date)) {
        return { valid: false, reason: `Not in same day ${activityTime.dayOfWeek}`, value: 0 };
    }
    const midnight = start >= end;
    const duration = getDurationOfTimeRange({ start, end, midnight });
    let intersection = duration;
    if (activityTime.open && activityTime.close) {
        intersection = getDurationBetweenRanges({ start, end, midnight }, {
            start: activityTime.open,
            end: activityTime.close,
            midnight: activityTime.open >= activityTime.close,
        });
        const isValid = intersect ? intersection > 0 : duration === intersection;
        if (!isValid) {
            return { valid: false, reason: `Not in open time ${activityTime.open} <=> ${activityTime.close}`, value: 0 };
        }
    }
    return { valid: true, value: intersection };
}
/**
 * Splits a time slot that spans across multiple days into multiple time slots.
 * @param slot {T} - The time slot to split.
 * @returns {ITimeSlot[]} The split time slots.
 */
function splitSlot(slot) {
    if (slot.startDate === slot.endDate ||
        (slot.endTime === MIDNIGHT && (0, validation_1.isSameDate)((0, dayjs_1.default)(slot.startDate).add(1, types_1.TimeUnit.DAY), slot.endDate))) {
        return [{ startDate: slot.startDate, endDate: slot.endDate, startTime: slot.startTime, endTime: slot.endTime }];
    }
    const slots = [];
    let currentStartTime = slot.startTime;
    let currentStartDate = slot.startDate;
    do {
        const currentSlot = {
            startDate: slot.startDate,
            endDate: slot.endDate,
            startTime: slot.startTime,
            endTime: slot.endTime,
        };
        currentSlot.startTime = currentStartTime;
        currentSlot.startDate = currentStartDate;
        currentSlot.endTime = currentStartDate === slot.endDate ? slot.endTime : MIDNIGHT;
        currentSlot.endDate = currentStartDate;
        slots.push(currentSlot);
        currentStartTime = MIDNIGHT;
        currentStartDate = (0, dayjs_1.default)(currentStartDate).add(1, types_1.TimeUnit.DAY).format(types_1.DateTimeFormats.YYYY_MM_DD);
    } while ((0, validation_1.isSameDateOrEarlier)(currentStartDate, slot.endDate));
    return slots;
}
exports.splitSlot = splitSlot;
/**
 * Calculates the duration of a given time range in minutes.
 *
 * @param {ITimeRange} range - The time range to calculate the duration for.
 * @returns {number} The duration of the time range in minutes. If the end time is before the start time,
 * the duration is adjusted to account for the time range spanning across midnight.
 */
function getDurationOfTimeRange(range) {
    const minutesRange = convertTimeRangeToMinutes(range);
    return minutesRange.end - minutesRange.start;
}
exports.getDurationOfTimeRange = getDurationOfTimeRange;
/**
 * Converts a time range represented in hours and minutes to a time range represented in total minutes.
 *
 * @param timeRange - The time range object containing start and end times in hours and minutes.
 * @returns An object containing the start and end times converted to total minutes.
 */
function convertTimeRangeToMinutes(timeRange) {
    return {
        start: timeInMinutes(timeRange.start),
        // Converts the end time of the time range to minutes and adds an extra day's worth of minutes if the time range spans across midnight.
        end: timeInMinutes(timeRange.end) + MINUTES_IN_24_HOURS * (Number(timeRange.midnight) || 0),
    };
}
/**
 * Converts a given time string into the total number of minutes.
 *
 * @param time - The time string in the format "HH:MM:SS".
 * @returns The total number of minutes as a number.
 */
function timeInMinutes(time) {
    const formatedTime = (0, formatter_1.formatTime)(time);
    const [hours, minutes, seconds] = formatedTime.split(':').map(Number);
    return Math.ceil(hours * MINUTES_IN_HOUR + minutes + seconds / SECONDS_IN_MINUTE);
}
/**
 * Checks if a given date is the same day as the day of the week of an activity time.
 * @param activityTime {T} - The activity time.
 * @param date {string} - The date to compare.
 */
function isSameDayAsActivity(activityTime, date) {
    const day = (0, dayjs_1.default)(date).day();
    return activityTime.dayOfWeek === types_1.DAYS_OF_WEEK_TO_BOND_DAYS_OF_WEEK[day];
}
/**
 * Calculates the overlapping duration in minutes between two time ranges.
 *
 * @param {ITimeRange} first - The first time range.
 * @param {ITimeRange} second - The second time range.
 * @returns {number} The duration in minutes of the overlap between the two time ranges.
 */
function getDurationBetweenRanges(first, second) {
    const firstMinutes = convertTimeRangeToMinutes(first);
    const secondMinutes = convertTimeRangeToMinutes(second);
    if (firstMinutes.end > secondMinutes.start && secondMinutes.end > firstMinutes.start) {
        const start = Math.max(firstMinutes.start, secondMinutes.start);
        const end = Math.min(firstMinutes.end, secondMinutes.end);
        return end - start;
    }
    return 0;
}
//# sourceMappingURL=slot-duration.js.map